import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

const hasErrors = (errors = {}) => Object.values(errors).filter(Boolean).length === 0;

const useValidation = <T>(initialValues: T, validationSchema, onSubmit) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Partial<T>>({});
  const [isSubmitting, setSubmitting] = useState(false);
  const initialDataRef = useRef(initialValues);
  const validationSchemaRef = useRef(validationSchema);
  const { t } = useTranslation();

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setValues((old) => ({ ...old, [name]: value }));
  }, []);

  const setValue = useCallback((key: string, value) => {
    setValues((old) => ({ ...old, [key]: value }));
  }, []);

  const setDataValue = useCallback((values) => setValues(values), []);

  const checkErrors = useCallback(() => {
    if (!validationSchemaRef.current) {
      return false;
    }
    const schemaWithTranslations = validationSchemaRef.current(t);
    const errors = schemaWithTranslations.cast(values);
    setErrors(errors);

    return hasErrors(errors);
  }, [validationSchemaRef, values, t]);

  const handleSubmit = useCallback(() => {
    checkErrors();
    setSubmitting(true);
  }, [checkErrors]);

  const resetToDefault = useCallback(() => setValues(initialDataRef.current), [initialDataRef]);

  const resetErrors = useCallback(() => setErrors({}), []);

  useEffect(() => {
    if (isSubmitting) {
      const noErrors = hasErrors(errors);

      if (noErrors) {
        onSubmit(values, {
          setValue,
          setDataValue,
          resetToDefault,
        });
      }

      setSubmitting(false);
    }
  }, [errors, isSubmitting, values, onSubmit, setValue, resetToDefault, setDataValue]);

  return {
    handleSubmit,
    handleChange,
    setValue,
    setDataValue,
    resetToDefault,
    resetErrors,
    errors,
    values,
  };
};

export default useValidation;
